import * as React from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { ArrowSyncRegular, InfoRegular, ChevronDownRegular } from "@fluentui/react-icons";
import { useTestingAgentClient } from "../api/TestingAgentApiClient";
import type { ProjectListItem } from "../api/models/ApiModels";
import { useProjectPickerStyles } from "../styles/projectPicker.styles";
import { useProjectDashboardPageStyles } from "../styles/projectDashboard.styles";
import { useTenant } from "../auth/TenantContext";
import { usePlaywrightWorkspaces } from "../hooks/usePlaywrightWorkspaces";
import { Header } from "./Header";
import EmptyStateLanding from "./EmptyStateLanding";

/** Extract workspace name from the Azure resource ID (last path segment). */
function extractWorkspaceName(resourceId: string): string {
    const trimmed = resourceId.replace(/\/+$/, "");
    const lastSegment = trimmed.split("/").pop();
    return lastSegment || "Workspace";
}

/** Format a date string for display. Returns "—" for invalid/missing dates. */
function formatLastRun(dateStr?: string | null): string {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
        " – " +
        d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

const ProjectPicker: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { getProjects } = useTestingAgentClient();
    const { selectedTenantId } = useTenant();
    const s = useProjectPickerStyles();
    const ds = useProjectDashboardPageStyles();

    const resourceId = searchParams.get("resourceId") ?? "";

    // Auto-redirect to last opened project on initial app load (no resourceId, no explicit back nav)
    const didRedirectRef = React.useRef(false);
    React.useEffect(() => {
        if (didRedirectRef.current) return;
        didRedirectRef.current = true;
        if (resourceId) return;
        const explicitBack = (location.state as { fromBack?: boolean } | null)?.fromBack;
        if (explicitBack) return;
        const lastUrl = (selectedTenantId && localStorage.getItem(`dashboard.lastProjectUrl.${selectedTenantId}`))
            || localStorage.getItem("dashboard.lastProjectUrl");
        if (lastUrl) {
            navigate(lastUrl, { replace: true });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [projects, setProjects] = React.useState<ProjectListItem[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [hasLoaded, setHasLoaded] = React.useState(false);
    const [refreshKey, setRefreshKey] = React.useState(0);
    const [searchQuery, setSearchQuery] = React.useState("");

    const { workspaces } = usePlaywrightWorkspaces(resourceId);
    const [wsDropdownOpen, setWsDropdownOpen] = React.useState(false);
    const [wsSearchQuery, setWsSearchQuery] = React.useState("");
    const wsDropdownRef = React.useRef<HTMLDivElement>(null);
    const wsSearchRef = React.useRef<HTMLInputElement>(null);

    // Close workspace dropdown on outside click
    React.useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wsDropdownRef.current && !wsDropdownRef.current.contains(e.target as Node)) {
                setWsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Clear stale data when resourceId changes
    React.useEffect(() => {
        setSearchQuery("");
    }, [resourceId]);

    React.useEffect(() => {
        if (resourceId) void loadProjects(resourceId);
    }, [resourceId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadProjects = async (id: string) => {
        setLoading(true);
        setError(null);
        try {
            const result = await getProjects(id.trim());
            setProjects(result.value ?? []);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to load projects");
        } finally {
            setLoading(false);
            setHasLoaded(true);
        }
    };

    // No-project empty state: show Getting Started when no resourceId provided OR when projects list is empty
    if ((!resourceId && !loading) || (!loading && hasLoaded && projects.length === 0 && !error)) {
        return (
            <div className={ds.page}>
                <Header projectName="Testing Agent" />
                <div className={ds.emptyBody}>
                    <div className={ds.emptyMainContent}>
                        <div className={ds.titleRow}>
                            <span className={ds.titleText}>Test Dashboard</span>
                            <div className={ds.titleSpacer} />
                            <button className={ds.refreshBtn} onClick={() => setRefreshKey((k) => k + 1)}>
                                <ArrowSyncRegular style={{ fontSize: 16 }} /> Refresh
                            </button>
                        </div>
                        <EmptyStateLanding />
                    </div>
                    <EmptyStateLanding.Sidebar />
                </div>
            </div>
        );
    }

    const workspaceName = extractWorkspaceName(resourceId);
    const filtered = projects.filter(p =>
        !searchQuery || (p.name ?? "").toLowerCase().includes(searchQuery.toLowerCase())
    );
    const countLabel = searchQuery
        ? `${filtered.length} of ${projects.length} projects`
        : `${projects.length} project${projects.length !== 1 ? "s" : ""}`;

    return (
        <div className={s.page}>
            <Header projectName="Testing Agent" resourceId={resourceId} />
            <div className={s.body}>
                <div className={s.mainContent}>
                    {/* Title row */}
                    <div className={s.titleRow}>
                        <span className={s.titleText}>App Testing Projects</span>
                        <div className={s.titleSpacer} />
                        <button className={s.refreshBtn} onClick={() => setRefreshKey(k => k + 1)}>
                            <ArrowSyncRegular style={{ fontSize: 16 }} /> Refresh
                        </button>
                    </div>

                    {/* Toolbar + Table section */}
                    <div className={s.tableSection}>
                        <div className={s.toolbar}>
                            <span className={s.workspaceLabel}>
                                Workspace <InfoRegular style={{ fontSize: 14 }} />
                            </span>
                            <div className={s.workspaceDropdownWrapper} ref={wsDropdownRef}>
                                <button
                                    className={s.workspaceDropdownBtn}
                                    onClick={() => {
                                        setWsDropdownOpen(o => {
                                            if (!o) {
                                                setWsSearchQuery("");
                                                setTimeout(() => wsSearchRef.current?.focus(), 0);
                                            }
                                            return !o;
                                        });
                                    }}
                                    disabled={workspaces.length <= 1}
                                >
                                    {workspaceName}
                                    {workspaces.length > 1 && (
                                        <ChevronDownRegular style={{
                                            fontSize: 12,
                                            marginLeft: "auto",
                                            transform: wsDropdownOpen ? "rotate(180deg)" : "none",
                                            transition: "transform 0.15s",
                                        }} />
                                    )}
                                </button>
                                {wsDropdownOpen && workspaces.length > 1 && (
                                    <div className={s.workspaceDropdownMenu}>
                                        <input
                                            ref={wsSearchRef}
                                            className={s.workspaceDropdownSearch}
                                            placeholder="Search workspaces…"
                                            value={wsSearchQuery}
                                            onChange={e => setWsSearchQuery(e.target.value)}
                                            onClick={e => e.stopPropagation()}
                                        />
                                        {workspaces
                                            .filter(w => !wsSearchQuery || w.name.toLowerCase().includes(wsSearchQuery.toLowerCase()))
                                            .map(w => (
                                            <button
                                                key={w.id}
                                                className={`${s.workspaceDropdownItem}${
                                                    w.id.toLowerCase().replace(/\/+$/, "") === resourceId.toLowerCase().replace(/\/+$/, "")
                                                        ? ` ${s.workspaceDropdownItemActive}` : ""
                                                }`}
                                                onClick={() => {
                                                    setWsDropdownOpen(false);
                                                    if (w.id.toLowerCase().replace(/\/+$/, "") !== resourceId.toLowerCase().replace(/\/+$/, "")) {
                                                        setSearchParams(prev => {
                                                            const next = new URLSearchParams(prev);
                                                            next.set("resourceId", w.id);
                                                            return next;
                                                        });
                                                    }
                                                }}
                                            >
                                                {w.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <span className={s.projectCount}>{countLabel}</span>
                            <input
                                className={s.searchInput}
                                placeholder="Search projects…"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>

                        {/* Loading / Error */}
                        {loading && <div className={s.loading}>Loading projects…</div>}
                        {error && <div className={s.error}>{error}</div>}

                        {/* Projects table */}
                        {!loading && hasLoaded && (
                            <div className={s.tableWrapper}>
                                <table className={s.table}>
                                    <thead>
                                        <tr>
                                            <th className={s.th}>Name</th>
                                            <th className={s.th}>Last Run</th>
                                            <th className={s.thAction}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.length === 0 ? (
                                            <tr>
                                                <td colSpan={3} className={s.emptyTable}>
                                                    {projects.length === 0 ? "No projects found" : "No matching projects"}
                                                </td>
                                            </tr>
                                        ) : (
                                            filtered.map(p => (
                                                <tr key={p.projectId} className={s.tr}>
                                                    <td className={s.td}>{p.name ?? "—"}</td>
                                                    <td className={s.tdMuted}>{formatLastRun(p.systemData?.lastModifiedAt)}</td>
                                                    <td className={s.tdAction}>
                                                        <button
                                                            onClick={() =>
                                                                navigate(
                                                                    `/dashboard/${encodeURIComponent(resourceId)}/${encodeURIComponent(p.projectId ?? "")}`,
                                                                    { state: { projectName: p.name ?? undefined } },
                                                                )
                                                            }
                                                            className={s.openBtn}
                                                        >
                                                            Open
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectPicker;
